import { useState } from 'react';
import { useCustom, useMany } from '@refinedev/core';
import { Card, Flex, Segmented, Select, Table } from 'antd';
import dayjs from 'dayjs';
import type {
  OrganizationOut,
  PublisherListOut,
  TopOrganizationOut,
  TopProductOut,
  TopPublisherOut,
} from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationsByProducts } from '@/components/user_catalog/publications/usePublicationsByProducts';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { formatLedger } from '@/utils/money';
import { formatDuration } from '@/utils/duration';
import { cleanFilters, type SettlementStatsFilters } from './filters';

const PERIODS = [
  {
    label: 'Bieżący miesiąc',
    value: dayjs().startOf('month').format('YYYY-MM-DD'),
  },
  {
    label: 'Poprzedni miesiąc',
    value: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
  },
];

type RankingTab = 'organizations' | 'publishers' | 'products';

// Broadest first: organizations sum their publishers' charges.
const TABS: { key: RankingTab; label: string }[] = [
  { key: 'organizations', label: 'Organizacje (należności)' },
  { key: 'publishers', label: 'Wydawcy (należności)' },
  { key: 'products', label: 'Produkty (czas odsłuchu)' },
];

const LIMIT_OPTIONS = [5, 10, 25, 50].map((n) => ({
  label: `Top ${n}`,
  value: n,
}));

type Props = { filters: SettlementStatsFilters };

export const SettlementRankingsCard = ({ filters }: Props) => {
  const [tab, setTab] = useState<RankingTab>('organizations');
  const [limit, setLimit] = useState(10);
  const [period, setPeriod] = useState<string>(PERIODS[0]!.value);

  const rankQuery = { period, limit, ...cleanFilters(filters) };

  // Only the visible ranking is fetched.
  const { query: organizationsQuery } = useCustom<TopOrganizationOut[]>({
    url: 'settlement/charges_monthly/stats/top_organizations',
    method: 'get',
    config: { query: rankQuery },
    queryOptions: { enabled: tab === 'organizations' },
  });

  const { query: publishersQuery } = useCustom<TopPublisherOut[]>({
    url: 'settlement/charges_monthly/stats/top_publishers',
    method: 'get',
    config: { query: rankQuery },
    queryOptions: { enabled: tab === 'publishers' },
  });

  const { query: productsQuery } = useCustom<TopProductOut[]>({
    url: 'settlement/charges_monthly/stats/top_products',
    method: 'get',
    config: { query: rankQuery },
    queryOptions: { enabled: tab === 'products' },
  });

  const organizationRows = organizationsQuery.data?.data ?? [];
  const publisherRows = publishersQuery.data?.data ?? [];
  const productRows = productsQuery.data?.data ?? [];

  const organizationIds = organizationRows.map((r) => r.organization_id);

  const { result: organizationsResult } = useMany<OrganizationOut>({
    resource: 'tenancy.organizations',
    ids: organizationIds,
    queryOptions: { enabled: organizationIds.length > 0 },
  });

  const organizationMap = new Map<number, OrganizationOut>(
    (organizationsResult?.data ?? []).map((o: OrganizationOut) => [o.id, o])
  );

  const publisherIds = [
    ...new Set([
      ...publisherRows.map((r) => r.publisher_id),
      ...productRows.map((r) => r.publisher_id),
    ]),
  ];

  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });

  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const productIds = [
    ...new Set(productRows.map((r) => r.product_id).filter(Boolean)),
  ];

  const publicationByProduct = usePublicationsByProducts(productIds);

  return (
    <Card
      title="Ranking"
      tabList={TABS}
      activeTabKey={tab}
      onTabChange={(key) => setTab(key as RankingTab)}
      extra={
        <Flex gap="small" wrap>
          <Select<number>
            options={LIMIT_OPTIONS}
            value={limit}
            onChange={setLimit}
            style={{ width: 110 }}
          />
          <Segmented
            options={PERIODS.map((p) => ({ label: p.label, value: p.value }))}
            value={period}
            onChange={(v) => setPeriod(v as string)}
          />
        </Flex>
      }
    >
      {tab === 'organizations' && (
        <Table
          dataSource={organizationRows as TopOrganizationOut[]}
          rowKey="organization_id"
          pagination={false}
          size="small"
          loading={organizationsQuery.isFetching}
        >
          <Table.Column title="#" render={(_v, _r, i) => i + 1} width={40} />
          <Table.Column
            dataIndex="organization_id"
            title="Organizacja"
            render={(id: number) => (
              <ShowIconLink resource="tenancy.organizations" id={id}>
                {organizationMap.get(id)?.name ?? id}
              </ShowIconLink>
            )}
          />
          <Table.Column
            dataIndex="basis_seconds"
            title="Czas odsłuchu"
            render={(v: number) => formatDuration(v)}
          />
          <Table.Column
            dataIndex="amount"
            title="Należność"
            render={(v: string) => formatLedger(v)}
          />
        </Table>
      )}

      {tab === 'publishers' && (
        <Table
          dataSource={publisherRows as TopPublisherOut[]}
          rowKey="publisher_id"
          pagination={false}
          size="small"
          loading={publishersQuery.isFetching}
        >
          <Table.Column title="#" render={(_v, _r, i) => i + 1} width={40} />
          <Table.Column
            dataIndex="publisher_id"
            title="Wydawca"
            render={(id: number) => (
              <PublisherIconLink
                publisherId={id}
                publisher={publisherMap.get(id)}
              />
            )}
          />
          <Table.Column
            dataIndex="basis_seconds"
            title="Czas odsłuchu"
            render={(v: number) => formatDuration(v)}
          />
          <Table.Column
            dataIndex="amount"
            title="Należność"
            render={(v: string) => formatLedger(v)}
          />
        </Table>
      )}

      {tab === 'products' && (
        <Table
          dataSource={productRows as TopProductOut[]}
          rowKey={(r) => `${r.product_id}`}
          pagination={false}
          size="small"
          loading={productsQuery.isFetching}
        >
          <Table.Column title="#" render={(_v, _r, i) => i + 1} width={40} />
          <Table.Column
            dataIndex="publisher_id"
            title="Wydawca"
            render={(id: number) => (
              <PublisherIconLink
                publisherId={id}
                publisher={publisherMap.get(id)}
              />
            )}
          />
          <Table.Column
            dataIndex="product_id"
            title="Produkt"
            render={(id: number) => {
              const publication = publicationByProduct.get(id);
              const kind = productKind(publication, id);
              return (
                <Flex gap={4} align="center" wrap>
                  {publication ? (
                    <PublicationInfoPopover
                      publication={publication}
                      publicationId={publication.id}
                    />
                  ) : (
                    id
                  )}
                  {kind && <ProductFormatTag kind={kind} />}
                </Flex>
              );
            }}
          />
          <Table.Column
            dataIndex="basis_seconds"
            title="Czas odsłuchu"
            render={(v: number) => formatDuration(v)}
          />
          <Table.Column
            dataIndex="amount"
            title="Należność"
            render={(v: string) => formatLedger(v)}
          />
        </Table>
      )}
    </Card>
  );
};
